import {
    ISharingInformation, ISharingLink, ISharingPrincipal, SharingAudience,
    SharingLinkKind, SharingRole, Error, ISharingContext,
    IShareLinkContext, IUnshareLinkContext, IShareResult,
    IUpdatePermissionsResponse, UpdatePermissionsRole,
    IRecipient, Category, Mode, IShareLinkResult,
    ISharingContextInformation, SharingCapabilityDisabledReason,
    IShareLinkParams,
    ClientId
} from '../../interfaces/SharingInterfaces';
import ISharingStore from '../../interfaces/ISharingStore';
import ISharingLinkSettings from '../../interfaces/ISharingLinkSettings';
import IPolicyTipInformation from '../../interfaces/IPolicyTipInformation';
import AccessStatus from '../../interfaces/enums/AccessStatus';
import IDefaultRecipients from '../../interfaces/IDefaultRecipients';
import OutlookAvailability from '../../interfaces/enums/OutlookAvailability';

import { ISharingProvider } from '@ms/odsp-datasources/lib/Sharing';
import {
    IPeoplePickerProvider, IPickerEntityInformation,
    IPerson, EntityType
} from '@ms/odsp-datasources/lib/PeoplePicker';
import PrincipalType from '@ms/odsp-datasources/lib/dataSources/roleAssignments/PrincipalType';
import {
    IPolicyTipInfo, PolicyTipUserAction, PolicyTipUserActionResult,
    IPolicyTipProvider, IUpdatePolicyTipContext
} from '@ms/odsp-datasources/lib/PolicyTip';
import { PolicyTipType } from '@ms/odsp-datasources/lib/dataSources/item/spListItemProcessor/SPListItemEnums';
import { ISuiteNavProvider, ISuiteNavData } from '@ms/odsp-datasources/lib/SuiteNav';
import { IGraphPeoplePickerHelper } from '@ms/odsp-datasources/lib/utilities/peoplePicker/GraphPeoplePickerHelper';
import { ISubstratePeoplePickerHelper } from '@ms/odsp-datasources/lib/utilities/peoplePicker/SubstratePeoplePickerHelper';
import Guid from '@ms/odsp-utilities/lib/guid/Guid';
import Promise from '@ms/odsp-utilities/lib/async/Promise';
import Features from '@ms/odsp-utilities/lib/features/Features';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import EventGroup from '@ms/odsp-utilities/lib/events/EventGroup';
import stringsResx from '../../components/Share.resx';
import { Killswitch } from '@ms/odsp-utilities/lib/killswitch/Killswitch';
import QosEvent, { ResultTypeEnum } from '@ms/odsp-utilities/lib/logging/events/Qos.event';
import isPSLExperimentOn from '@ms/odsp-datasources/lib/utilities/peopleSharingLinks/PSLExperimentHelper'
import ISharingService from '@ms/odsp-graph/lib/services/sharing/SharingService';
import { IPermission } from '@ms/odsp-graph/lib/models/permission/Permission';
import { getSharingLinkKindFromVroomScope, getSharingAudienceFromVroomScope } from '../../utilities/ShareHelper';
import { getClientId } from '@ms/odsp-datasources/lib/interfaces/sharing/enums/ClientId';
import SimpleUri from '@ms/odsp-utilities/lib/uri/SimpleUri';
import * as ClientIdHelper from '../../utilities/ClientIdHelper';

// Features
declare const window: Window & {
    _sharingContextInfo?: { webAbsoluteUrl: string; };
};

// Enable UI on specific Heartbeat Tenant.
const isShareHeartbeatTenant = (window._sharingContextInfo && window._sharingContextInfo.webAbsoluteUrl) ?
    (window._sharingContextInfo.webAbsoluteUrl.toLowerCase().indexOf('a830edad9050849880j18121110') >= 0) : false;

const PasswordProtectedLinks = { ODB: 868, OneDrive: isShareHeartbeatTenant };
const PreventDownloadLink = { ODB: 780, OneDrive: isShareHeartbeatTenant };

export interface ISharingStoreParams<T> {
    item: T;
    sharingInformation: ISharingInformation;
    defaultRecipients: string[];
    accessChecked: boolean;
    mode: Mode;
    sharingContextInformation?: ISharingContextInformation;
    sharingService?: ISharingService
}

export interface ISharingStoreDependencies<T> {
    sharingProvider: ISharingProvider<T>;
    /**
     * suiteNavProvider is optional. If not provided, the sharing dialog will use
     * "your organization" instead of the company name and will not show the button
     * to go to Outlook.
     */
    suiteNavProvider?: ISuiteNavProvider;
    peoplePickerProvider: IPeoplePickerProvider;
    /**
     * policyTipProvider is optional. If not provided, users will not see
     * detailed policy tip information if they are blocked from sharing
     * sensitive content due to organizational policy.
     */
    policyTipProvider?: IPolicyTipProvider<T>;
    graphPeoplePickerHelper?: IGraphPeoplePickerHelper;
    substratePeoplePickerHelper?: ISubstratePeoplePickerHelper;
}

export interface ItemWithPolicyTip {
    policyTip?: PolicyTipType;
}

interface ISharingStoreContents {
    sharingInformation: ISharingInformation;
    sharingLinkCreated: ISharingLink;
    companyName: string;
    groupsMemberCount: number;
    policyTipInfo: IPolicyTipInformation;
    permissionsMap: { [index: string]: AccessStatus };
    invitedUsers: IPerson[];
    defaultLink: ISharingLink; // The default link created with "Copy link" command.
    defaultRecipients: IDefaultRecipients;
    outlookAvailability: OutlookAvailability;
    grantAccessResult: boolean;
    addPeopleResult: boolean;
    accessRequestCreated: boolean;
    isValidatingDefaultRecipients: boolean;
}

interface IApiStats {
    name: string;
    duration: number;
    resultType: ResultTypeEnum
    error?: Error;
}

export default class SharingStore<T extends ItemWithPolicyTip> implements ISharingStore {
    private _firstShareLinkRequest: boolean;
    private _item: T;
    private _listeners: Array<() => void> = null;
    private _sharingProvider: ISharingProvider<T>;
    private _store: ISharingStoreContents;
    private _suiteNavProvider: ISuiteNavProvider;
    private _graphPeoplePickerHelper: IGraphPeoplePickerHelper;
    private _substratePeoplePickerHelper: ISubstratePeoplePickerHelper;
    private _peoplePickerProvider: IPeoplePickerProvider;
    private _groupsMemberCountCache: Array<number>;
    private _policyTipProvider: IPolicyTipProvider<T>;
    private _sharingInformationInvalidated: boolean;
    private _recipients: Array<string>;
    private _invitedUsers: Array<string>;
    private _sharingContextInformation: ISharingContextInformation;
    private _eventGroup: EventGroup;
    private _groupMembersCountHash: { [groupEmail: string]: number };
    private _peopleSharingLinks: boolean;
    private _getSharingInformationStats: IApiStats;
    private _sharingService: ISharingService;
    private _mode: Mode;
    private _clientId: ClientId;

    constructor(params: ISharingStoreParams<T>, dependencies: ISharingStoreDependencies<T>) {
        const {
            item,
            sharingInformation,
            defaultRecipients,
            accessChecked = true, // Assume access has been checked if not told otherwise.
            mode
        } = params;
        this._item = item;
        this._mode = mode;
        this._sharingContextInformation = params.sharingContextInformation;
        this._clientId = this._sharingContextInformation && getClientId(this._sharingContextInformation.clientId);
        this._sharingProvider = dependencies.sharingProvider;
        this._suiteNavProvider = dependencies.suiteNavProvider || undefined;
        this._graphPeoplePickerHelper = dependencies.graphPeoplePickerHelper;
        this._substratePeoplePickerHelper = dependencies.substratePeoplePickerHelper;
        this._peoplePickerProvider = dependencies.peoplePickerProvider;
        this._policyTipProvider = dependencies.policyTipProvider || undefined;

        this._sharingService = params.sharingService;
        this._firstShareLinkRequest = true;
        this._listeners = [];
        this._groupsMemberCountCache = [];
        this._sharingInformationInvalidated = false;
        this._groupMembersCountHash = {};
        this._peopleSharingLinks = isPSLExperimentOn;

        this._store = {
            sharingInformation: sharingInformation,
            sharingLinkCreated: null,
            companyName: (this._sharingContextInformation && this._sharingContextInformation.organizationName) || stringsResx.companyNameFallback,
            groupsMemberCount: 0,
            policyTipInfo: {
                info: null,
                actionResult: null
            },
            permissionsMap: {},
            invitedUsers: [],
            defaultLink: null,
            defaultRecipients: null,
            outlookAvailability: OutlookAvailability.none,
            grantAccessResult: null,
            addPeopleResult: null,
            accessRequestCreated: false,
            isValidatingDefaultRecipients: false
        };

        if (!defaultRecipients || defaultRecipients.length === 0) {
            this._eventGroup = new EventGroup(this);
            this._eventGroup.on(window, 'message', (event: MessageEvent) => {
                if (event && event.data) {
                    try {
                        const message = JSON.parse(event.data);

                        if (message.name === 'atMention_defaultRecipients') {
                            const recipientIds = message.recipientIds;
                            this._resolveDefaultRecipients(recipientIds, accessChecked, mode);
                        }
                    } catch (exception) {
                        // Ignore.
                    }
                }
            });
        } else {
            this._resolveDefaultRecipients(defaultRecipients, accessChecked, mode);
        }
    }

    public addListener(listener: () => void) {
        this._listeners.push(listener);
    }

    public getSharingInformation(): ISharingInformation {
        return this._store.sharingInformation;
    }

    public getPSLExperimentOn(): boolean {
        return this._peopleSharingLinks;
    }

    public fetchSharingInformation(needsPermissionsInformation: boolean): void {
        // If we already have sharingInformation in the store, emit change right away and return.
        if (this._store.sharingInformation && !this._sharingInformationInvalidated && (!needsPermissionsInformation || this._store.sharingInformation.hasPermissionsInformation)) {
            this._getSharingInformationStats = {
                name: 'GetSharingInformationProvided',
                resultType: ResultTypeEnum.Success,
                duration: 0
            }
            this._emitChange();
            return;
        }
        let startTime = new Date().getTime();

        const expandPermissionsInformation = !!this._sharingContextInformation || needsPermissionsInformation;

        this._sharingProvider.getSharingInformation(this._item, expandPermissionsInformation, true /* We always want sharing link templates */).then((response: ISharingInformation) => {
            this._getSharingInformationStats = {
                name: 'GetSharingInformation',
                resultType: ResultTypeEnum.Success,
                duration: new Date().getTime() - startTime
            }
            if (!this._store.sharingInformation || !this._store.sharingInformation.error) {
                this._store.sharingInformation = response;
                this._fetchAdditionalAuthenticationTokens();
                this.fetchPolicyTipInformation();
                this._emitChange();
            }
        }, (errorResponse: any) => {
            let error = Error.generic;
            const errorCode = errorResponse && errorResponse.code;
            const errorStatus = errorResponse && errorResponse.status;
            const errorMessage = (errorResponse && errorResponse.message && errorResponse.message.value) || stringsResx.getSharingInformationError;
            const debugErrorMessage = (errorResponse && errorResponse.responseData && errorResponse.responseData.error_description) || errorMessage;
            const argumentException = '-2147024809';

            if (errorCode) {
                if (errorCode.indexOf(argumentException) > -1) {
                    if (errorMessage && errorMessage.value) {
                        if (errorMessage.value.indexOf('Item does not exist') > -1) {
                            error = Error.invalidItem;
                        }
                    }
                }
            } else if (errorStatus) {
                if (parseInt(errorStatus, 10) >= 500) {
                    error = Error.serverFailure;
                }
            }
            this._getSharingInformationStats = {
                name: 'GetSharingInformation',
                resultType: ResultTypeEnum.Failure,
                duration: new Date().getTime() - startTime,
                error: error
            };
            this._invokeShareStatsEvent();

            this._store.sharingInformation = {
                error: error,
                errorCategory: Category.getSharingInformation,
                errorMessage: errorMessage,
                debugErrorMessage: `${errorStatus} - ${debugErrorMessage}`,
                anonymousLinkExpirationRestrictionDays: undefined,
                canAddExternalPrincipal: undefined,
                canAddInternalPrincipal: undefined,
                canManagePermissions: undefined,
                defaultSharingLink: undefined,
                isShared: undefined,
                item: undefined,
                sharingLinks: undefined,
                sharingPrincipals: undefined,
                peoplePickerSettings: undefined,
                userDisplayName: undefined,
                blockPeoplePickerAndSharing: undefined,
                defaultShareLinkPermission: undefined,
                sharingAbilities: undefined,
                defaultLinkKind: undefined,
                directLink: undefined,
                hasPermissionsInformation: undefined,
                isGraphAvailable: false,
                sharingLinkTemplates: undefined,
                webTemplateId: undefined,
                customizedExternalSharingServiceUrl: undefined
            };
            this._emitChange();
        });
    }

    public fetchMemberCount(group: IPerson): void {
        if (!this._groupMembersCountHash[group.email]) {
            this._peoplePickerProvider.getPickerEntityInformation(group)
                .then((entityInformation: IPickerEntityInformation) => {
                    const groupCount = entityInformation.totalMemberCount;
                    this._groupMembersCountHash[group.email] = groupCount;
                    this._emitChange();
                })
                .catch((error) => {
                    this._groupMembersCountHash[group.email] = null;
                    this._emitChange();
                });
        }
    }

    public getMembersCounts(): { [groupEmail: string]: number } {
        return this._groupMembersCountHash;
    }

    /**
     * Shares a link to the selected item.
     */
    public shareLink(shareLinkParams: IShareLinkParams): void {
        const isFirstShareLinkRequest = this._firstShareLinkRequest;
        this._firstShareLinkRequest = false;
        let {
            settings,
            recipients,
            emailMessage,
            emailSubject,
            copyLinkCommand,
            allRecipientsHaveAccess,
            shareId,
            inviteesToRemove,
        } = shareLinkParams;

        if (shareLinkParams.copyLinkCommand &&
            ClientIdHelper.isTeams(this._clientId) &&
            this._mode === Mode.copy &&
            this._sharingService &&
            (this._sharingContextInformation.itemUrl || (this._sharingContextInformation.webAbsoluteUrl && this._sharingContextInformation.serverRelativeItemUrl)) &&
            isFirstShareLinkRequest) {
            this._getDefaultLink();
            return;
        }

        // Convert recipients so types match.
        recipients = recipients || [];
        let internalRecipientsCount = 0;
        let externalRecipientsCount = 0;
        const convertedRecipients = recipients.map((person: IPerson): IPerson => {
            person.entityType === EntityType.externalUser ? externalRecipientsCount++ : internalRecipientsCount++;

            return {
                userId: person.userId,
                name: person.name,
                email: person.email,
                rawPersonData: person.rawPersonData
            };
        });

        /**
         * If shareID exists, then we are editing the link (manage access).
         * Use ShareLink.
         */
        if (!Killswitch.isActivated("88cc94ae-ba5f-4552-aea0-f5a00a24ff6b", "01/02/2019", "Ensure share link is called for manage access")
            && shareId && shareId.length > 0) {
            this._shareLink(settings, convertedRecipients, emailMessage, emailSubject, copyLinkCommand, isFirstShareLinkRequest, shareId, inviteesToRemove);
        } else {

            let useShareLink = ((this._peopleSharingLinks && settings.audience !== SharingAudience.existing) || settings.audience === SharingAudience.customLabeledSpecificPeople);

            /**
             * - If flights are on and we're not in an access request scenario, then we
             *   use ShareLink.
             * - If flights are off or flights are on and we're in an access request scenario,
             *   then we fall back to ShareObject.
             */
            if (useShareLink && !this._store.sharingInformation.requiresAccessApproval) {
                this._shareLink(settings, convertedRecipients, emailMessage, emailSubject, copyLinkCommand, isFirstShareLinkRequest, shareId, inviteesToRemove);
            } else {
                /**
                 * - If it's not "Specific people", use ShareLink, unless it's flexible,
                 *   which means access requests are turned on and we can't use ShareLink.
                 * - If it's "Specific people", use ShareObject (but return different link
                 *   based on recipients).
                 */
                if (settings.sharingLinkKind !== SharingLinkKind.direct &&
                    settings.sharingLinkKind !== SharingLinkKind.flexible) {
                    this._shareLink(settings, convertedRecipients, emailMessage, emailSubject, copyLinkCommand, isFirstShareLinkRequest, shareId, inviteesToRemove);
                } else {
                    let roleString: string;
                    if (!settings.sharingRole) {
                        roleString = this._getRoleValue(settings.isEdit);
                    } else {
                        roleString = this._getRoleString(settings.sharingRole);
                    }

                    const sharingContext: ISharingContext<T> = {
                        items: [this._item],
                        roleValue: roleString,
                        sendEmail: emailMessage !== undefined,
                        message: emailMessage,
                        subject: emailSubject,
                        requireSignIn: true,
                        propagateSharing: true
                    };

                    const directLink = this._store.sharingInformation.directLink;

                    /**
                     * If there are no recipients being permissioned, we can just return the
                     * canonical link. Otherwise, do permissioning (via ShareObject) and THEN
                     * return the canonical link if there are no invitation links or the invitation
                     * link if one exists.
                     */
                    if (recipients.length === 0 ||
                        (settings.audience == SharingAudience.existing) && (this._mode === Mode.copy || this._mode == Mode.linkSettings)) {
                        this._invokeShareStatsEvent({
                            name: 'NoExtraCallReturnCanonical',
                            duration: 0,
                            resultType: ResultTypeEnum.Success
                        });
                        this._handleSharedLink(directLink, copyLinkCommand);
                    } else {
                        // Check recipients to determine if all are permissioned.
                        let allUsersArePermissioned = true;
                        for (const recipient of recipients) {
                            if (this._store.permissionsMap[recipient.email] !== AccessStatus.hasAccess &&
                                this._store.permissionsMap[recipient.userId] !== AccessStatus.hasAccess) {
                                allUsersArePermissioned = false;
                            }
                        }
                        /**
                         * If all users are permissioned, user "emailWithoutPermissioning" to just email
                         * the link on. If there are users that aren't permissioned, use "share" so permissioning
                         * is done.
                         */
                        if (allUsersArePermissioned || allRecipientsHaveAccess) {
                            let forwardObjectLinkStartTime = new Date().getTime();
                            this._sharingProvider.emailWithoutPermissioning(convertedRecipients, this._item, emailMessage, emailSubject).done((result: IShareResult) => {
                                const statusCode = result.statusCode; // SharingOperationStatusCode in sporel\sts
                                if (statusCode === 0 /* Successful share */) {
                                    this._invokeShareStatsEvent({
                                        name: 'ForwardObjectLink',
                                        duration: new Date().getTime() - forwardObjectLinkStartTime,
                                        resultType: ResultTypeEnum.Success
                                    });
                                    this._handleSharedLink(directLink, copyLinkCommand);
                                } else /* An error occurred */ {
                                    const strings = stringsResx;

                                    // Try to get the most specific error code we can so the error message is helpful.
                                    let error = Error.generic;
                                    let errorMessage = strings.getSharingInformationError;
                                    if (statusCode === -8) {
                                        error = Error.objectNotSupported;
                                        errorMessage = strings.sharingOperationNotSupportedError;
                                    } else if (statusCode === -13) {
                                        error = Error.invalidEmail;
                                        errorMessage = StringHelper.format(strings.invalidEmailErrorMessage, result.invalidEmails.join(', '));
                                    } else if (statusCode === -7) {
                                        error = Error.generic;
                                        errorMessage = result.errorMessage;
                                    }

                                    this._invokeShareStatsEvent({
                                        name: 'ForwardObjectLink',
                                        duration: new Date().getTime() - forwardObjectLinkStartTime,
                                        resultType: ResultTypeEnum.Failure,
                                        error: error
                                    });

                                    this._store.sharingInformation = {
                                        ...this._store.sharingInformation,
                                        error: error,
                                        errorCategory: Category.shareObject,
                                        errorMessage: errorMessage
                                    };

                                    this._emitChange();
                                }
                            });
                        } else {
                            let shareObjectStartTime = new Date().getTime();
                            this._sharingProvider.share(convertedRecipients, sharingContext, true /* viaV3 */).done((result: IShareResult) => {
                                const statusCode = result.statusCode; // SharingOperationStatusCode in sporel\sts
                                if (statusCode === 0 /* Successful share */) {
                                    /**
                                     * - If it's a single or multiple internal recipients (without
                                     *   any external recipients) return the canonical link.
                                     * - If it's a single external recipient (without any internal
                                     *   recipients) return that user's invitation link.
                                     * - If it's multiple external recipients (without any internal
                                     *   recipients) return the invitation link without the email
                                     *   query param.
                                     */
                                    let linkToReturn = directLink;
                                    if (internalRecipientsCount === 0 && externalRecipientsCount === 1 && result.invitationLink) {
                                        linkToReturn = {
                                            ...directLink,
                                            url: result.invitationLink
                                        };
                                    } else if (internalRecipientsCount === 0 && externalRecipientsCount > 1 && result.invitationLink) {
                                        linkToReturn = {
                                            ...directLink,
                                            url: this._removeEmailQueryParamFromInvitationLink(result.invitationLink)
                                        };
                                    }

                                    if (settings.audience === SharingAudience.customLabeledSpecificPeople) {
                                        linkToReturn = {
                                            ...linkToReturn,
                                            audience: SharingAudience.customLabeledSpecificPeople
                                        };
                                    }

                                    this._invokeShareStatsEvent({
                                        name: 'ShareObject',
                                        resultType: ResultTypeEnum.Success,
                                        duration: new Date().getTime() - shareObjectStartTime
                                    });

                                    this._handleSharedLink(linkToReturn, copyLinkCommand);
                                } else if (statusCode === 1 /* Access request generated */) {
                                    // Still not right, but waiting on design for this one (access requests).
                                    this._store.accessRequestCreated = true;

                                    this._invokeShareStatsEvent({
                                        name: 'ShareObject',
                                        resultType: ResultTypeEnum.Success,
                                        duration: new Date().getTime() - shareObjectStartTime
                                    });

                                    this._handleSharedLink(directLink, copyLinkCommand);
                                } else /* An error occurred */ {
                                    const strings = stringsResx;

                                    // Try to get the most specific error code we can so the error message is helpful.
                                    let error = Error.generic;
                                    let errorMessage = strings.getSharingInformationError;
                                    if (statusCode === -8) {
                                        error = Error.objectNotSupported;
                                        errorMessage = strings.sharingOperationNotSupportedError;
                                    } else if (statusCode === -13) {
                                        error = Error.invalidEmail;
                                        errorMessage = StringHelper.format(strings.invalidEmailErrorMessage, result.invalidEmails.join(', '));
                                    } else if (statusCode === -7) {
                                        error = Error.generic;
                                        errorMessage = result.errorMessage;
                                    }

                                    this._store.sharingInformation = {
                                        ...this._store.sharingInformation,
                                        error: error,
                                        errorCategory: Category.shareObject,
                                        errorMessage: errorMessage
                                    };

                                    this._invokeShareStatsEvent({
                                        name: 'ShareObject',
                                        resultType: ResultTypeEnum.Failure,
                                        duration: new Date().getTime() - shareObjectStartTime,
                                        error: error
                                    });

                                    this._emitChange();
                                }
                            });
                        }
                    }
                }
            }

            // Clean up default link created with "Copy link" command only if the audience changes
            if (copyLinkCommand && this._store.defaultLink && this._store.defaultLink.audience !== settings.audience) {
                this._cleanupDefaultLink();
            }
        }
    }

    public unshareLink(linkKind: SharingLinkKind, shareId: string): void {
        const unshareLinkContext: IUnshareLinkContext<T> = {
            item: this._item,
            kind: linkKind,
            shareId: shareId
        };

        this._sharingProvider.unshareLink(unshareLinkContext).done((unshareLinkResponse: boolean) => {
            // If response is true, unshare was successful so remove link.
            if (unshareLinkResponse === true) {
                this._removeLinkByShareId(shareId);
                this._updateIsShared();
                this._emitChange();
            } else {
                // TODO (joem): Error handling.
            }
        });
    }

    public getSharingLinkCreated(): ISharingLink {
        return this._store.sharingLinkCreated;
    }

    public updatePermissions(entity: any, role: SharingRole): void {
        this._sharingProvider.updatePermissions2(entity, this._item, role).done((result: IUpdatePermissionsResponse) => {
            let principals: Array<ISharingPrincipal> = this._store.sharingInformation.sharingPrincipals;

            // If new role is 0 (i.e. no longer being shared with), remove that
            // user from the principals collection.
            //
            // Otherwise, iterate over principals and update the one that was
            // changed.
            if (result.role === UpdatePermissionsRole.NONE) {
                this._store.sharingInformation.sharingPrincipals = principals.filter((principal: ISharingPrincipal) => {
                    return principal.id !== entity.id;
                });

                this._updateIsShared();
                this._emitChange();
            } else {
                for (const principal of principals) {
                    if (principal.id === entity.id) {
                        // Change role and emit change.
                        switch (result.role) {
                            case UpdatePermissionsRole.VIEW:
                                principal.role = SharingRole.view;
                                break;
                            case UpdatePermissionsRole.EDIT:
                                principal.role = SharingRole.edit;
                                break;
                            case 6: /* Review Role */
                                principal.role = SharingRole.review;
                                break;
                        }
                        this._emitChange();
                    }
                }
            }
        });
    }

    public addPeople(people: Array<IPerson>, role: SharingRole, requestApproval: boolean, notify: boolean, message?: string): void {

        // An eventual switch to using updateDocumentSharingInfo is anticipated so that path has been left in the code.
        const forceShareObject = true;

        if (requestApproval || forceShareObject) {
            let roleString: string = this._getRoleString(role);
            const context: ISharingContext<T> = {
                items: [this._item],
                roleValue: roleString,
                sendEmail: notify,
                message: message,
                requireSignIn: true,
                propagateSharing: true
            };
            this._sharingProvider.share(people, context, true).then(
                (result: IShareResult) => { this._store.addPeopleResult = result.statusCode === 0; this._emitChange(); },
                () => { this._store.addPeopleResult = false; this._emitChange(); });

        } else {
            this._sharingProvider.updatePermissionsMultiple(people, this._item, role, notify, message).then((result: IUpdatePermissionsResponse) => {
                let principals: Array<ISharingPrincipal> = this._store.sharingInformation.sharingPrincipals;

                // If new role is 0 (i.e. no longer being shared with), remove all updated users from the principles collection
                //
                // Otherwise, iterate over principals and update the one that was
                // changed.
                if (result.role === UpdatePermissionsRole.NONE) {
                    this._store.sharingInformation.sharingPrincipals = principals.filter((principal: ISharingPrincipal) => {
                        // return true if principle is not in the people array based in the userid field
                        return people.map(person => person.userId).indexOf(principal.id.toString()) === -1;
                    });

                    this._updateIsShared();
                } else {
                    for (const principal of principals) {
                        if (people.map(person => person.userId).indexOf(principal.id.toString()) !== -1) {
                            // Change role and emit change.
                            switch (result.role) {
                                case UpdatePermissionsRole.VIEW:
                                    principal.role = SharingRole.view;
                                    break;
                                case UpdatePermissionsRole.EDIT:
                                    principal.role = SharingRole.edit;
                                    break;
                                case 6: /* Review Role */
                                    principal.role = SharingRole.review;
                                    break;
                            }
                        }
                    }
                }

                this._store.addPeopleResult = result.success;
                this._emitChange();
            },
                () => { this._store.addPeopleResult = false; this._emitChange(); });
        }
    }

    public fetchSuiteNavInformation(): void {
        /**
         * If no suiteNavProvider was given, use "your organization" instead of the actual company name
         * and do not show the button to go to Outlook.
         */
        if (this._suiteNavProvider) {
            this._suiteNavProvider.loadData().then((suiteNavData: ISuiteNavData) => {
                const companyName = suiteNavData.CompanyDisplayName || this._store.companyName;
                const clientData = suiteNavData ? JSON.parse(suiteNavData.ClientData) : { HasEXOLicense: false };

                this._store.companyName = companyName;
                this._store.outlookAvailability = clientData.HasEXOLicense ? OutlookAvailability.available : OutlookAvailability.none;
                this._emitChange();
            });
        } else {
            this._store.outlookAvailability = OutlookAvailability.none;
            this._emitChange();
        }
    }

    public getCompanyName(): string {
        return this._store.companyName;
    }

    public getOutlookAvailability(): OutlookAvailability {
        return this._store.outlookAvailability;
    }

    public navigateToOwa(recipients: Array<string>, sharingLinkUrl: string) {
        window.open(this._getOwaUrl(recipients, sharingLinkUrl), '_blank');
    }

    public fetchGroupsMemberCount(items: Array<IPerson>): void {
        let count = 0;

        for (const item of items) {
            const person: IPerson = {
                userId: item.userId,
                name: item.name,
                email: item.email,
                rawPersonData: item.rawPersonData,
                principalType: item.principalType
            };

            if (item.principalType === PrincipalType.securityGroup) {
                const cachedCount = this._groupsMemberCountCache[item.userId];

                if (cachedCount !== undefined) {
                    count += cachedCount;
                } else {
                    this._peoplePickerProvider.getPickerEntityInformation(person)
                        .then((entityInformation: IPickerEntityInformation) => {
                            const groupCount = entityInformation.totalMemberCount;

                            this._groupsMemberCountCache[item.userId] = groupCount;
                            count += groupCount;

                            this._store.groupsMemberCount = count;
                            this._emitChange();
                        })
                        .catch((error) => {
                            this._groupsMemberCountCache[item.userId] = null;
                            this._emitChange();
                        });
                }
            }
        }

        this._store.groupsMemberCount = count;
        this._emitChange();
    }

    public getGroupsMemberCount(): number {
        return this._store.groupsMemberCount;
    }

    public fetchPolicyTipInformation() {
        if (this._policyTipProvider) {
            /**
             * If policyTipInfo was already retrieved, use the cached version
             * and return.
             */
            if (this._store.policyTipInfo.info) {
                this._emitChange();
                return;
            }

            /**
             * If there isn't any sharing information yet, just return. It'll
             * be attempted again when sharing information is received.
             */
            const sharingInformation = this._store.sharingInformation;
            if (!sharingInformation) {
                return;
            }

            if (!this._item) {
                return;
            }

            /**
             * If sharingAbilities is present, use it to determine if external
             * sharing is blocked by DLP. Otherwise, rely on the item itself.
             */
            const itemPolicyCanShare = !this._isPolicyTipFlagSet(this._item.policyTip, PolicyTipType.blockSharing);
            const sharingAbilities = sharingInformation.sharingAbilities;
            if (sharingAbilities) {
                const disabledReason = sharingAbilities && sharingAbilities.directSharingAbilities.canAddExternalPrincipal.disabledReason;

                if (disabledReason !== SharingCapabilityDisabledReason.documentIsProtectedByDlp && itemPolicyCanShare) {
                    return;
                }
            } else if (itemPolicyCanShare) {
                return;
            }

            /**
             * If the function reaches this point, it means that external sharing is
             * blocked by DLP. If so, fetch details about the policy tip so we can
             * inform the user if they attempt to share externally.
             */
            this._policyTipProvider.getPolicyTip(this._item).then((policyTipInfo: IPolicyTipInfo) => {
                if (policyTipInfo) {
                    this._store.policyTipInfo.info = policyTipInfo;
                    this._emitChange();
                }
            });
        }
    }

    public getPolicyTipInformation(): IPolicyTipInformation {
        return this._store.policyTipInfo;
    }

    public updatePolicy(action: PolicyTipUserAction, justificationText?: string): void {
        if (this._policyTipProvider) {
            const context: IUpdatePolicyTipContext<T> = {
                item: this._item,
                action: action
            };

            if (justificationText) {
                context.justification = justificationText;
            }

            this._policyTipProvider.updatePolicy(context).then((result: PolicyTipUserActionResult) => {
                this._store.policyTipInfo.actionResult = result;
                this._sharingInformationInvalidated = true;
                this.fetchSharingInformation(true /* expandPermissionsInformation */);
                this._emitChange();
            });
        }
    }

    public checkPermissions(recipients: Array<IPerson>, invitedUsers?: Array<IPerson>) {
        // If user cleared people picker, clear permissions map and return without calling API.
        if (recipients.length === 0) {
            this._store.permissionsMap = {};
            this._emitChange();
            return;
        }

        const recipientAliases: Array<IRecipient> = [];
        this._recipients = [];
        for (const recipient of recipients) {
            recipientAliases.push({
                alias: !Killswitch.isActivated('00bdc590-2d96-4d10-bb1c-66fb6cb59dfd') ? (recipient.userId || recipient.email) : recipient.email
            });

            this._recipients.push(recipient.email);
        }

        invitedUsers = invitedUsers || this._store.invitedUsers;
        this._invitedUsers = [];
        for (const invitedUser of invitedUsers) {
            recipientAliases.push({
                alias: invitedUser.email
            });

            this._invitedUsers.push(invitedUser.email);
        }

        const aliases = recipientAliases.map(((entry: IRecipient) => entry.alias));

        this._sharingProvider.checkPermissions(recipientAliases, this._item)
            .then((response: any) => {
                this._store.permissionsMap = {};

                for (const entry of response) {
                    /**
                     * Checks state of recipients when API call completes. Only update permissionsMap
                     * if entry is still in people picker.
                     */
                    if (this._invitedUsers.indexOf(entry.inputEntity) > -1) {
                        this._store.permissionsMap[entry.inputEntity] = entry.hasAccess ? AccessStatus.hasAccess : AccessStatus.pending;
                    } else if (this._recipients.indexOf(entry.inputEntity) > -1) {
                        this._store.permissionsMap[entry.inputEntity] = entry.hasAccess ? AccessStatus.hasAccess : AccessStatus.none;
                    } else if (aliases.indexOf(entry.inputEntity) > -1) {
                        this._store.permissionsMap[entry.inputEntity] = entry.hasAccess ? AccessStatus.hasAccess : AccessStatus.none;
                    }
                }

                this._emitChange();
            });
    }

    public getPermissionsMap() {
        return this._store.permissionsMap;
    }

    public getDefaultRecipients(): IDefaultRecipients {
        return this._store.defaultRecipients;
    }

    public consumeDefaultRecipients() {
        this._store.defaultRecipients = {
            isConsumed: true,
            recipients: this._store.defaultRecipients.recipients
        };
    }

    public grantAccess(recipients: IPerson[]) {
        // Convert recipients so types match.
        recipients = recipients || [];
        const convertedRecipients = recipients.map((person: IPerson): IPerson => {
            return {
                userId: person.userId,
                name: person.name,
                email: person.email,
                rawPersonData: person.rawPersonData
            };
        });

        const sharingContext: ISharingContext<T> = {
            items: [this._item],
            roleValue: this._getRoleValue(true),
            sendEmail: false,
            requireSignIn: true,
            propagateSharing: true
        };

        this._sharingProvider.share(convertedRecipients, sharingContext, true /* viaV3 */).done((result: IShareResult) => {
            // A statusCode of 0 is the only one that means recipients were permissioned.
            this._store = {
                ...this._store,
                grantAccessResult: result.statusCode === 0
            };

            this._emitChange();
        });
    }

    public getGrantAccessResult(): boolean {
        return this._store.grantAccessResult;
    }

    public getAddPeopleResult(): boolean {
        return this._store.addPeopleResult;
    }

    public dispose(): void {
        if (this._eventGroup) {
            this._eventGroup.dispose();
            this._eventGroup = undefined;
        }
    }

    public hasAccessRequestBeenCreated(): boolean {
        return this._store.accessRequestCreated;
    }

    public isValidatingDefaultRecipients(): boolean {
        return this._store.isValidatingDefaultRecipients;
    }
    private _cleanupDefaultLink() {
        const defaultLink = this._store.defaultLink;

        if (defaultLink) {
            this.unshareLink(defaultLink.sharingLinkKind, defaultLink.shareId);
            this._store.defaultLink = undefined;
        }
    }

    /**
     * Let listeners know that the store has new data.
     */
    private _emitChange(): void {
        for (const listener of this._listeners) {
            listener();
        }
    }

    private _fetchAdditionalAuthenticationTokens() {
        if (!this._store.sharingInformation) {
            return;
        }

        /**
         * The initial authentication call can take a little bit to complete. Make the call here so that by
         * the time the user starts searching the call will have completed and there will be less delay
         * for search results. If the auth call fails or Graph calls are disabled, this will also update
         * the UI to hide the button to search for more.
         */
        let canUseGraph = this._graphPeoplePickerHelper && this._graphPeoplePickerHelper.canUseGraph();
        if (canUseGraph) {
            this._graphPeoplePickerHelper.getGraphToken().then((oAuthToken) => {
                canUseGraph = !!oAuthToken;
                this._store.sharingInformation.isGraphAvailable = canUseGraph;
            });
        } else {
            this._store.sharingInformation.isGraphAvailable = false;
        }

        let canUseSubstrate = this._substratePeoplePickerHelper && this._substratePeoplePickerHelper.canUseSubstrate();
        if (canUseSubstrate) {
            this._substratePeoplePickerHelper.getSubstrateToken().then((oAuthToken) => {
                this._substratePeoplePickerHelper.init(oAuthToken);
                canUseSubstrate = !!oAuthToken;
                this._store.sharingInformation.isTsAvailable = canUseSubstrate;
            });
        } else {
            this._store.sharingInformation.isTsAvailable = false;
        }
    }

    private _handleSharedLink(link: ISharingLink, copyLinkCommand?: boolean) {
        // Add creation information to link.
        link.createdViaCopyLinkCommand = copyLinkCommand || false;

        // Add created sharing link to store.
        this._store.sharingLinkCreated = link;

        // Update isShared.
        this._updateIsShared();

        // Emit change.
        this._emitChange();
    }

    private _getRoleValue(isEdit: boolean): string {
        return isEdit ? 'role:1073741827' : 'role:1073741826';
    }

    private _getRoleString(role: SharingRole): string {
        if (role === SharingRole.review) {
            return 'roletype:Reviewer';
        } else if (role === SharingRole.edit) {
            return 'role:1073741827';
        } else {
            return 'role:1073741826';
        }
    }

    private _removeLinkByShareId(shareId: string) {
        // Clear properties of created link.
        this._store.sharingInformation.sharingLinks = this._store.sharingInformation.sharingLinks.map((link: ISharingLink) => {
            if (shareId === link.shareId) {
                // Clear details.
                link.expiration = null;
                link.isActive = false;
                link.shareId = Guid.Empty;
                link.url = null;

                // Clear principals.
                link.principals = [];
            }

            return link;
        });
    }

    private _updateIsShared() {
        const sharingInformation = this._store.sharingInformation;
        if (!sharingInformation) {
            return;
        }
        const sharingLinks = sharingInformation.sharingLinks;
        const sharingPrincipals = sharingInformation.sharingPrincipals;
        let isShared = false;

        // Check if there are any active sharing links.
        for (const sharingLink of sharingLinks) {
            if (sharingLink.isActive && sharingLink.sharingLinkKind !== SharingLinkKind.direct) {
                isShared = true;
            }
        }

        // If there are no links, check if any entities have been directly ACL'ed to the item.
        if (!isShared) {
            isShared = sharingPrincipals.length > 0;
        }

        // Update the store with the value.
        this._store.sharingInformation.isShared = isShared;
    }

    private _getOwaUrl(recipients: Array<string>, sharingLinkUrl: string): string {
        const isGallatin = location.origin.indexOf('.cn') === location.origin.length - 3;
        const isBlackForest = location.origin.indexOf('.de') === location.origin.length - 3;
        let urlRoot = 'https://outlook.office.com/owa/';
        if (isGallatin) {
            urlRoot = 'https://partner.outlook.cn/owa/';
        } else if (isBlackForest) {
            urlRoot = 'https://outlook.de/owa/';
        }

        const body = encodeURIComponent(sharingLinkUrl);
        const outlookUrl = `${urlRoot}?body=${body}&to=${recipients.join(';')}&path=/mail/action/compose`;
        return outlookUrl;
    }

    private _resolveDefaultRecipients(emailAddresses: string[], accessChecked: boolean, mode: Mode) {
        let emailAddressesWithoutAccess = emailAddresses || [];
        const checkAccessPromise = [];

        this._store.isValidatingDefaultRecipients = emailAddresses.length > 0;

        /**
         * If access hasn't been checked, do so. Clear the array of email addresses,
         * and only add email addresses of users that don't have access.
         */
        if (!accessChecked) {
            // Clear email addresses here.
            emailAddressesWithoutAccess = [];

            /**
             * Transform email addresses into IRecipient objects that CheckPermissions
             * needs.
             */
            const recipientAliases: Array<IRecipient> = [];
            for (const emailAddress of emailAddresses) {
                recipientAliases.push({
                    alias: emailAddress
                });
            }

            /**
             * Dissect the response of CheckPermissions, only adding recipients without
             * access to the array of email addresses to resolve.
             */
            checkAccessPromise.push(this._sharingProvider.checkPermissions(recipientAliases, this._item)
                .then((response: any) => {
                    for (const entry of response) {
                        if (!entry.hasAccess) {
                            emailAddressesWithoutAccess.push(entry.inputEntity);
                        }
                    }
                }));
        }

        /**
         * Once CheckPermissions is complete (or if we didn't call it), take the
         * array of email addresses that we've determined don't have permission,
         * and resolve them.
         */
        Promise.all(checkAccessPromise).then(() => {
            /**
             * If there are no email addresses without access AND we're in the
             * @-mention scenario, set the result as if all users were correctly
             * permissioned.
             *
             * If we're not in the @-mention scenario, simply return.
             */
            if (emailAddressesWithoutAccess.length === 0) {
                if (mode === Mode.atMention) {
                    this._store = {
                        ...this._store,
                        grantAccessResult: true
                    };
                    this._emitChange();
                }

                return;
            }

            const resolveUserPromises = [];
            const resolvedDefaultRecipients = [];

            const peoplePickerQueryParams: any = {
                principalSource: 15,
                principalType: 13,
                allowEmailAddresses: true,
                filterExternalUsers: false,
                maximumEntitySuggestions: 1,
                allowSharePointGroups: false,
                forceResolve: true
            };

            /**
             * Iterate over array of email addresses that represent
             * the default recipients for the share UI, and resolve
             * the email addresses.
             */
            for (const emailAddress of emailAddressesWithoutAccess) {
                resolveUserPromises.push(this._peoplePickerProvider.resolve(emailAddress, peoplePickerQueryParams).then((resolvedDefaultRecipient: IPerson) => {
                    resolvedDefaultRecipients.push(resolvedDefaultRecipient);
                }));
            }

            /**
             * Wait for all of the promises to finish and store the
             * array of resolved IPerson objects in the store.
             */
            Promise.all(resolveUserPromises).then(() => {
                this._store.defaultRecipients = {
                    isConsumed: false,
                    recipients: resolvedDefaultRecipients
                };
                this._store.isValidatingDefaultRecipients = false;
                this._emitChange();
            }, () => {
                // Unable to resolve the default recipients
                this._store.defaultRecipients = {
                    isConsumed: false,
                    recipients: []
                };
                this._store.isValidatingDefaultRecipients = false;
                this._emitChange();
            });
        });
    }

    private _removeEmailQueryParamFromInvitationLink(invitationLink: string): string {
        // Split link with query params.
        const linkParts = invitationLink.split('?');

        // If there's a query string, continue. Otherwise, just return the input.
        if (linkParts.length > 1) {
            // Split query string into params.
            const allQueryParams = linkParts[1].split('&');
            const goodQueryParams = [];

            /**
             * Iterate over query string params, and add all query string params
             * that aren't "email" to a new array.
             */
            for (const queryParam of allQueryParams) {
                if (queryParam.indexOf('email=') === -1) {
                    goodQueryParams.push(queryParam);
                }
            }

            // Create a new query string with the non-"email" params.
            const goodQueryParamsString = goodQueryParams.join('&');

            // Re-create the invitation link using the base and the good query string.
            return `${linkParts[0]}?${goodQueryParamsString}`;
        } else {
            return invitationLink;
        }
    }

    private _shareLink(settings: ISharingLinkSettings, recipients: Array<IPerson>, emailMessage: string, emailSubject: string, copyLinkCommand: boolean, isFirstShareLinkRequest: boolean, shareId: string, inviteesToRemove: Array<ISharingPrincipal>) {
        const context: IShareLinkContext<T> = {
            emailData: emailMessage,
            emailSubject: emailSubject,
            items: [this._item],
            recipients: recipients,
            settings: {
                sharingLinkKind: settings.sharingLinkKind,
                expiration: settings.expiration,
                updatePassword: settings.updatePassword,
                password: settings.password,
                shareId: shareId,
                inviteesToRemove: inviteesToRemove,
            }
        };
        let startTime = new Date().getTime();
        var apiQos = new QosEvent({ name: `SharingShareLink.${Mode[this._mode]}.${(ClientId[this._clientId] || 'unknown')}` });

        // If updating the password make sure that you set it to Flex Link Kind and set the right Audience as well
        // If we've provided a shareId in settings, and there isn't an already existing one, use it
        if (settings.updatePassword || (settings.hasPassword && settings.shareId && settings.shareId.length > 0)) {
            if (settings.audience === SharingAudience.anyone) {
                context.settings.sharingLinkKind = SharingLinkKind.flexible;
                context.settings.allowAnonymousAccess = true;
                if (!settings.sharingRole) {
                    context.settings.role = settings.isEdit ? SharingRole.edit : SharingRole.view;
                } else {
                    context.settings.role = settings.sharingRole;
                }
            }
            if (settings.shareId && settings.shareId.length > 0) {
                context.settings.shareId = settings.shareId;
            }
        }

        if (settings.audience === SharingAudience.anyone) {
            context.settings.allowAnonymousAccess = true;
            context.settings.restrictShareMembership = false;
        } else if (settings.audience === SharingAudience.organization) {
            context.settings.allowAnonymousAccess = false;
            context.settings.restrictShareMembership = false;
        }

        /**
         * Main logic for setting correct SharingRole and LinkKind if
         * "Block Download" is enabled.
         */
        if (settings.preventDownload) {
            context.settings.role = SharingRole.restrictedview;
            settings.sharingRole = SharingRole.restrictedview;
            context.settings.sharingLinkKind = SharingLinkKind.flexible;
        }

        // If creating a PSL, add additional settings that are necessary.
        if (settings.audience === SharingAudience.specificPeopleFlex ||
            settings.audience === SharingAudience.customLabeledSpecificPeople) {
            context.settings.restrictShareMembership = true;
            if (!settings.sharingRole) {
                context.settings.role = settings.isEdit ? SharingRole.edit : SharingRole.view;
            } else {
                context.settings.role = settings.sharingRole;
            }
        }

        this._sharingProvider.shareLink(context).done((shareLinkResult: IShareLinkResult) => {
            if (shareLinkResult.error) {
                this._store.sharingInformation = {
                    ...this._store.sharingInformation,
                    error: shareLinkResult.error,
                    errorCategory: Category.shareLink,
                    errorMessage: stringsResx.getSharingInformationError
                };
                apiQos.end({ resultType: ResultTypeEnum.Failure });
                this._invokeShareStatsEvent({
                    name: 'ShareLink',
                    resultType: ResultTypeEnum.Failure,
                    duration: new Date().getTime() - startTime,
                    error: shareLinkResult.error
                });

                this._emitChange();
                return;
            }

            apiQos.end({ resultType: ResultTypeEnum.Success });

            this._invokeShareStatsEvent({
                name: 'ShareLink',
                resultType: ResultTypeEnum.Success,
                duration: new Date().getTime() - startTime
            });

            // Extract link from result.
            const sharingLink = shareLinkResult.link;

            if (settings.audience === SharingAudience.customLabeledSpecificPeople) {
                sharingLink.audience = SharingAudience.customLabeledSpecificPeople;
            }

            if (sharingLink.sharingLinkKind === this._store.sharingInformation.defaultSharingLink.sharingLinkKind &&
                isFirstShareLinkRequest && copyLinkCommand) {

                if (!Killswitch.isActivated('4FE917A1-5DBE-4BEB-8145-41D71F6F3673', '9/20/2018', 'Use Share Id to test for existing link.')) {
                    if (this._store.sharingInformation.sharingLinks.filter((link: ISharingLink) => link.shareId === sharingLink.shareId).length === 0) {
                        this._store.defaultLink = sharingLink;
                    }
                } else {
                    /**
                     * Track link as "defaultLink" if it was not created prior to opening
                     * the "Copy Link" UI. Will be used for a one time link cleanup if the user changes the scope.
                     */
                    this._store.sharingInformation.sharingLinks.forEach((link: ISharingLink) => {
                        if (link.audience === sharingLink.audience &&
                            (Killswitch.isActivated('EC2649A7-64A3-4EDB-BB8F-71AC090528CB', '9/20/2018', 'Scope comparison against existing share links') || link.isEdit === sharingLink.isEdit) &&
                            !link.isActive) {
                            this._store.defaultLink = sharingLink;
                        }
                    });
                }
            }

            if (shareId) {
                this._sharingInformationInvalidated = true;
                this.fetchSharingInformation(true /* expandPermissionsInformation */);
                this._emitChange();
            } else {
                this._handleSharedLink(sharingLink, copyLinkCommand);
            }
        });
    }

    private _invokeShareStatsEvent(sharingApiStats?: IApiStats) {
        const statsQos = new QosEvent();
        let resultType = this._getSharingInformationStats && this._getSharingInformationStats.resultType;
        if (sharingApiStats) {
            resultType = sharingApiStats.resultType;
        }
        let extraData = {
            mode: Mode[this._mode],
            clientId: ClientId[this._clientId] || 'unknown',
            getData: JSON.stringify(this._getSharingInformationStats),
            setData: JSON.stringify(sharingApiStats),
        };

        statsQos.end({
            name: 'SharingStats',
            resultType: resultType,
            extraData: extraData
        });
    }

    /**
     * This function taken from PolicyTipHelper in odsp-next.
     * If we require additional functions, we should create a separate helper file.
     *
     * @param policyTip The policy tip type for the item you are checking
     * @param flag The flag you want to check to see if it is set for that item
     */
    private _isPolicyTipFlagSet(policyTip: PolicyTipType, flag: PolicyTipType): boolean {
        return policyTip && ((policyTip & flag) === flag);
    }

    private _getDefaultLink(): void {
        const apiQos = new QosEvent({ name: 'SharingGetDefaultLink' });
        let itemUrl = this._sharingContextInformation.itemUrl;
        if (!itemUrl) {
            const uri = new SimpleUri(this._sharingContextInformation.webAbsoluteUrl);
            itemUrl = `${uri.authority}${this._sharingContextInformation.serverRelativeItemUrl}`;
        }
        this._sharingService.getDefaultLink(itemUrl).then((permissions: IPermission[]) => {
            const permission = permissions[0];
            const link = permission.link;
            const canEdit = permission.roles && permission.roles.indexOf('write') >= 0;

            apiQos.end({ resultType: ResultTypeEnum.Success });
            this._handleSharedLink({
                allowsAnonymousAccess: link.scope === 'anonymous',
                audience: getSharingAudienceFromVroomScope(link.scope),
                expiration: permission.expirationDateTime,
                isActive: true,
                isEdit: canEdit,
                principals: [],
                shareId: permission.id,
                sharingLinkKind: getSharingLinkKindFromVroomScope(link.scope, canEdit),
                url: link.webUrl,
                createdViaCopyLinkCommand: true,
                invitees: [],
                updatePassword: false,
                password: ''
            }, true);
        }, (error: Error) => {
            apiQos.end({
                resultType: ResultTypeEnum.Failure,
                extraData: {
                    error: error
                }
            });
            this._store.sharingInformation = {
                error: error,
                errorCategory: Category.invite,
                errorMessage: stringsResx.getSharingInformationError,
                debugErrorMessage: '',
                anonymousLinkExpirationRestrictionDays: undefined,
                canAddExternalPrincipal: undefined,
                canAddInternalPrincipal: undefined,
                canManagePermissions: undefined,
                defaultSharingLink: undefined,
                isShared: undefined,
                item: undefined,
                sharingLinks: undefined,
                sharingPrincipals: undefined,
                peoplePickerSettings: undefined,
                userDisplayName: undefined,
                blockPeoplePickerAndSharing: undefined,
                defaultShareLinkPermission: undefined,
                sharingAbilities: undefined,
                defaultLinkKind: undefined,
                directLink: undefined,
                hasPermissionsInformation: undefined,
                isGraphAvailable: false,
                sharingLinkTemplates: undefined,
                webTemplateId: undefined,
                customizedExternalSharingServiceUrl: undefined
            };
            this._emitChange();
        });
    }

    public isAudienceModificationAllowed() {
        return true;
    }

    public isPasswordModificationAllowed() {
        return Features.isFeatureEnabled(PasswordProtectedLinks);
    }

    public isPreventDownloadModificationAllowed() {
        return Features.isFeatureEnabled(PreventDownloadLink);
    }

    public isExpirationDateModificationAllowed() {
        return true;
    }

    public isPremiumExperienceAllowed() {
        return true;
    }
}
